import Breadcrumbs from '@pelckmans/elementis/components/breadcrumbs';
import Button from '@pelckmans/elementis/components/button';
import List from '@pelckmans/elementis/components/list';
import Sidebar from '@pelckmans/elementis/components/sidebar';
import { useBreakpoint } from '@pelckmans/elementis/contexts/BreakpointsContext';
import SidebarIcon from '@pelckmans/elementis/icons/sidebar';
import { ComponentProps, useCallback, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { Node } from '../../api/table-of-content';
import { ANALYTICS_EVENT_SUBLOCATIONS, ANALYTICS_EVENT_TYPES } from '../../constants/analyticsEvents';
import useAnalytics from '../../hooks/useAnalytics';
import { useMaterialLinkBuilder } from '../../hooks/useMaterialLinkBuilder';
import useStudioGroup from '../../hooks/useStudioGroup';
import ModuleContext from '../../pages/material/_contexts/ModuleContext';
import SidebarVisibilityContext from '../../pages/material/_contexts/SidebarVisibilityContext';
import SubModuleBreadcrumb from '../../pages/material/components/breadcrumbs/SubModuleBreadcrumb';
import SuperModuleBreadcrumb from '../../pages/material/components/breadcrumbs/SuperModuleBreadcrumb';
import SuperModuleLevelPageBreadcrumb from '../../pages/material/components/breadcrumbs/SuperModuleLevelPageBreadcrumb';
import TableOfContentsContext from '../../pages/material/toc/_contexts/TableOfContentsContext';
import AppButtonList from '../../pages/material/toc/components/toc-page-header/AppButtonList';
import {
  withScrollThreshold,
  type WithScrollThresholdProps,
} from '../../shared-components/elementis/hoc/withScrollThreshold';

interface MappedNode extends Node {
  level: number;
}

function containsNode(node: Node, id: string): boolean {
  if (node.id === id) return true;
  return node.nodes?.some(child => containsNode(child, id)) ?? false;
}

interface MaterialSidebarProps extends WithScrollThresholdProps {
  overview: boolean;
  setOverviewMode: (isOverview: boolean) => void;
}

function MaterialSidebar({ overview, setOverviewMode, pastThresholdForSidebar }: MaterialSidebarProps) {
  const { t } = useTranslation();
  const { selectedModule } = useContext(ModuleContext);
  const { selectedNode, nodes } = useContext(TableOfContentsContext);
  const { buildMaterialTocLink } = useMaterialLinkBuilder();
  const { capture } = useAnalytics();
  const studioGroup = useStudioGroup();
  const breakpoint = useBreakpoint();

  const { isSidebarVisible, setSidebarVisibility } = useContext(SidebarVisibilityContext);

  const getAppearance = (node: MappedNode) => {
    if (node.id === selectedNode?.id) return 'active';

    return node.level === 0 ? 'prominent' : 'default';
  };

  const getNodeAvatar = (node: MappedNode) => {
    if (node.id === 'general') {
      return {
        name: selectedModule?.niceName || 'placeholder',
        title: selectedModule?.niceName || 'placeholder',
        src: selectedModule?.styling?.logo || 'placeholder',
      };
    }

    return node.level === 0
      ? {
          name: node.title,
          title: node.title,
          src: node.image || 'placeholder',
        }
      : undefined;
  };

  const onNodeClick = useCallback(
    (node: Node) => {
      if (node.id === selectedNode?.id) setOverviewMode(false);

      const subLocationId = selectedModule?.isExerciseModule
        ? ANALYTICS_EVENT_SUBLOCATIONS.PORTAAL_EXERCISE_MODULE_MATERIAL
        : ANALYTICS_EVENT_SUBLOCATIONS.PORTAAL_MATERIAL;

      capture({
        objectId: node.id,
        eventType: ANALYTICS_EVENT_TYPES.NODE_ACCESSED,
        subLocationId,
      });

      if (isSidebarVisible) setSidebarVisibility(false);
    },
    [
      selectedNode?.id,
      setOverviewMode,
      selectedModule?.isExerciseModule,
      capture,
      isSidebarVisible,
      setSidebarVisibility,
    ],
  );

  const renderNodes = (nodes: Node[], level = 0, parentPath: string[] = []): (JSX.Element | null)[] => {
    const childIsActive = selectedNode && nodes.some(n => containsNode(n, selectedNode?.id));

    return nodes.flatMap(node => {
      if (
        parentPath.length &&
        !(childIsActive || node.id === selectedNode?.id || parentPath[parentPath.length - 1] === selectedNode?.id)
      )
        return null;

      const newParentPath = [...parentPath, node.id];
      const mappedNode = { ...node, level };

      return [
        <List.Item
          key={node.id}
          avatar={getNodeAvatar(mappedNode)}
          title={node.id === 'general' ? t('TOC.GENERAL') : node.title}
          subText={node.displayPrefix ? node.prefix : undefined}
          subTextPlacement="top"
          level={mappedNode.level as ComponentProps<typeof List.Item>['level']}
          as="link"
          href={buildMaterialTocLink(node.id) || ''}
          onClick={() => onNodeClick(node)}
          appearance={getAppearance(mappedNode)}
        />,
        ...(node.nodes ? renderNodes(node.nodes, level + 1, newParentPath) : []),
      ];
    });
  };

  return (
    <Sidebar
      overview={overview}
      onOverviewChange={
        breakpoint === 'desktop' && selectedNode?.id ? (isOverview: boolean) => setOverviewMode(isOverview) : undefined
      }
    >
      <Sidebar.Header
        breadcrumbs={
          overview ? (
            <Breadcrumbs aria-label={t('MODULE_BREADCRUMB.A11Y_LABEL')}>
              <SuperModuleBreadcrumb />
              <SuperModuleLevelPageBreadcrumb />
              <SubModuleBreadcrumb />
            </Breadcrumbs>
          ) : undefined
        }
        density={overview && !pastThresholdForSidebar ? 'normal' : 'compact'}
        headerText={t('PAGES.TOC_LEVEL.SIDEBAR.TITLE')}
        buttonGroupProps={{
          'aria-label': undefined,
          role: 'group',
          children: (
            <AppButtonList
              density="normal"
              digibooksOnly={true}
              showDigibooks={studioGroup !== 'BAO'}
              key="app-button-list"
            />
          ),
        }}
        contextButton={
          selectedNode && (
            <Button startIcon={<SidebarIcon />} onClick={() => setSidebarVisibility(false)}>
              {t('PAGES.TOC_LEVEL.SIDEBAR.CONTEXT_BUTTON.CLOSE')}
            </Button>
          )
        }
      />
      <Sidebar.Content>
        <List
          showSeparators={false}
          isLoading={!selectedModule || !nodes.length}
          loadingLabel={t('PAGES.TOC_LEVEL.SIDEBAR.LOADING.A11Y_LABEL')}
          className="list-items"
        >
          {renderNodes(nodes)}
        </List>
      </Sidebar.Content>
    </Sidebar>
  );
}

export default withScrollThreshold(MaterialSidebar, true);
